import React, { useState, useEffect } from 'react'
import { useMedications } from '../../hooks/useMedications'

interface ProgressKpisProps {
  selectedMonth: number
  selectedYear: number
}

interface KpiData {
  totalTaken: number
  totalMissed: number
  totalActivities: number
  adherencePercentage: number
}

const ProgressKpis: React.FC<ProgressKpisProps> = ({ selectedMonth, selectedYear }) => {
  const [kpis, setKpis] = useState<KpiData>({
    totalTaken: 0,
    totalMissed: 0,
    totalActivities: 0,
    adherencePercentage: 0
  })
  const [loading, setLoading] = useState(true)

  const { getMonthlyHistory } = useMedications()

  useEffect(() => {
    const calculateKpis = async () => {
      setLoading(true)
      try {
        const activities = await getMonthlyHistory(selectedYear, selectedMonth)

        const totalTaken = activities.filter(
          a => (a as any).status === 'taken' || (a as any).type === 'medication_taken'
        ).length

        const totalMissed = activities.filter(a => (a as any).status === 'missed').length

        const totalActivities = activities.length

        // Calcular adherencia: taken / (taken + missed)
        // Si no hay missed, usar 100% si hay taken, o 0% si no hay nada
        const totalRelevant = totalTaken + totalMissed
        const adherencePercentage = totalRelevant > 0
          ? Math.round((totalTaken / totalRelevant) * 100)
          : (totalTaken > 0 ? 100 : 0)

        setKpis({
          totalTaken,
          totalMissed,
          totalActivities,
          adherencePercentage
        })
      } catch (error) {
        console.error('Error calculating KPIs:', error)
        setKpis({
          totalTaken: 0,
          totalMissed: 0,
          totalActivities: 0,
          adherencePercentage: 0
        })
      } finally {
        setLoading(false)
      }
    }

    calculateKpis()
  }, [selectedMonth, selectedYear, getMonthlyHistory])

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        📊 Resumen del Período
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
          <div className="text-sm text-green-700 dark:text-green-300 mb-1">
            Total Tomados
          </div>
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {kpis.totalTaken}
          </div>
        </div>

        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
          <div className="text-sm text-red-700 dark:text-red-300 mb-1">
            Total Perdidos
          </div>
          <div className="text-2xl font-bold text-red-600 dark:text-red-400">
            {kpis.totalMissed}
          </div>
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
          <div className="text-sm text-blue-700 dark:text-blue-300 mb-1">
            Total Actividades
          </div>
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {kpis.totalActivities}
          </div>
        </div>

        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
          <div className="text-sm text-purple-700 dark:text-purple-300 mb-1">
            Adherencia
          </div>
          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
            {kpis.adherencePercentage}%
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProgressKpis

